import { JSX, FC, useEffect, useState, SetStateAction, Dispatch } from "react";
import axios from "axios";
import * as ProductsAPI from "api/products";

// utils
import {
  generateCategoryChains,
  mergeDuplicateNodes,
  OptionType,
  transformDataToCategory
} from "utils/formatCategories";
import { handleError } from "utils/handleError";
import useCancelableDebounce from "hooks/useCancelableDebounce";
import { parseCategoryFilters } from "./utils/parseCategoryFilters";

// types
import { LabelInValueType } from "rc-select/lib/Select";
import { FilterCategoriesRequestType, ProductType } from "products/types";
import { ChildrenCategoryType } from "categories/types";
import { ProductsFiltersValuesType } from "app/types";
import { FormInstance } from "antd";

import UploadedProductsFiltersPanel from "./UploadedProductsFiltersPanel/UploadedProductsFiltersPanel";

interface IUploadedProductsFiltersBlockProps {
  form: FormInstance<ProductsFiltersValuesType>;
  values: ProductsFiltersValuesType;
  params: URLSearchParams;
  isLoading: boolean;
  selectedRows: ProductType[];
  setSearchValue: Dispatch<SetStateAction<string>>;
  showDeleteModal: (values: ProductType[], text: string) => void;
  previousFilters: ProductsFiltersValuesType;
  setPreviousFilters: Dispatch<SetStateAction<ProductsFiltersValuesType>>;
  isShowDeleteButton: boolean;
  isFiltersConfirmed: boolean;
  isTagFilterDeleted: boolean;
  setIsTagFilterDeleted: Dispatch<SetStateAction<boolean>>;
  setIsFiltersConfirmed: Dispatch<SetStateAction<boolean>>;
  countProductsForDelete: number;
}

const UploadedProductsFiltersBlock: FC<IUploadedProductsFiltersBlockProps> = ({
  form,
  values,
  params,
  isLoading,
  selectedRows,
  setSearchValue,
  showDeleteModal,
  previousFilters,
  setPreviousFilters,
  isShowDeleteButton,
  isFiltersConfirmed,
  isTagFilterDeleted,
  setIsTagFilterDeleted,
  setIsFiltersConfirmed,
  countProductsForDelete,
}): JSX.Element => {
  const [categoriesTree, setCategoriesTree] = useState<OptionType[]>(null);
  const [categoriesTPTree, setCategoriesTPTree] = useState<OptionType[]>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>(null);
  const [selectedCategoriesTP, setSelectedCategoriesTP] = useState<number[]>(null);

  useEffect(() => {
    if (categoriesTree?.length) {
      processCategoryFilters(categoriesTree, "category_supplier", "supplierCategory");
    }
  }, [categoriesTree]);

  useEffect(() => {
    if (categoriesTPTree?.length) {
      processCategoryFilters(categoriesTPTree, "category_bk", "categoryBK");
    }
  }, [categoriesTPTree]);

  useEffect(() => {
    // TODO в отдельном тикете будем передавать остальные выбранные фильтры в body
    getCategoriesTree({
      ...(selectedCategoriesTP?.length ? { categories_bk: selectedCategoriesTP } : {}),
    });
  }, [selectedCategoriesTP]);

  useEffect(() => {
    // TODO в отдельном тикете будем передавать остальные выбранные фильтры в body
    getCategoriesTPTree({
      ...(selectedCategories?.length ? { categories: selectedCategories } : {}),
    });
  }, [selectedCategories]);

  const { debouncedFunction: getCategoriesTree } = useCancelableDebounce((params, token) => {
    const { search, ...body } = params;

    ProductsAPI.setFilterSupplierCategoryInfo(body as FilterCategoriesRequestType, search, token)
      .then((res) => {
        const categoriesTree: ChildrenCategoryType[][] = generateCategoryChains(res?.data);

        const chains: OptionType[] = categoriesTree
          ?.flatMap((item: ChildrenCategoryType[]) => transformDataToCategory(item));

        setCategoriesTree(mergeDuplicateNodes(chains));
      })
      .catch((error: any) => !axios.isCancel(error)
        && handleError("Ошибка при загрузке списка категорий поставщика.", error));
  });

  const { debouncedFunction: getCategoriesTPTree } = useCancelableDebounce((params, token) => {
    const { search, ...body } = params;

    ProductsAPI.setFilterTPCategoryInfo(body as FilterCategoriesRequestType, search, token)
      .then((res) => {
        const categoriesTree: ChildrenCategoryType[][] = generateCategoryChains(res?.data);

        const chains: OptionType[] = categoriesTree
          ?.flatMap((item: ChildrenCategoryType[]) => transformDataToCategory(item));

        setCategoriesTPTree(mergeDuplicateNodes(chains));
      })
      .catch((error: any) => !axios.isCancel(error)
        && handleError("Ошибка при загрузке списка категорий БК.", error));
  });

  // Подстановка категорий в форму из url
  const processCategoryFilters = (
    tree: OptionType[],
    searchKey: string,
    formField: string
  ): void => {
    const filters: LabelInValueType[] = parseCategoryFilters(tree, searchKey, params);

    if (filters.length) {
      form.setFieldsValue({ [formField]: filters });
    }
  };

  return (
    <UploadedProductsFiltersPanel
      form={form}
      values={values}
      params={params}
      selectedRows={selectedRows}
      isLoading={isLoading}
      categoriesTree={categoriesTree}
      categoriesTPTree={categoriesTPTree}
      setSelectedCategories={setSelectedCategories}
      setSelectedCategoriesTP={setSelectedCategoriesTP}
      isFiltersConfirmed={isFiltersConfirmed}
      isShowDeleteButton={isShowDeleteButton}
      showDeleteModal={showDeleteModal}
      setSearchValue={setSearchValue}
      previousFilters={previousFilters}
      setPreviousFilters={setPreviousFilters}
      setIsFiltersConfirmed={setIsFiltersConfirmed}
      countProductsForDelete={countProductsForDelete}
      isTagFilterDeleted={isTagFilterDeleted}
      setIsTagFilterDeleted={setIsTagFilterDeleted}
    />
  );
};

export default UploadedProductsFiltersBlock;
